/**
 * Public verification page for HSA/FSA benefits administrators.
 *
 * URL: /verify/:token  — no login, no site gate, no PHI exposure beyond
 * patient initials + masked account number. Backed by a 90-day HMAC-signed
 * token minted whenever a patient emails the benefits-summary PDF.
 *
 * UX goal: an administrator opens the link from the email, sees one big
 * VERIFIED checkmark, gets just enough info to approve a reimbursement
 * claim, and can call/email GLPFlo for anything deeper. Designed to look
 * official and printable — many HSA admins paste this page into a
 * compliance folder.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
  ShieldCheck, Loader2, AlertTriangle, Phone, Mail, Globe, Pill, BadgeCheck, Calendar,
} from "lucide-react";

function fmtMoney(n) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(Number(n || 0));
  } catch {
    return `$${Number(n || 0).toFixed(2)}`;
  }
}

function fmtVerifiedAt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function BenefitsVerifyPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Token endpoint is fully public — strip any auth/tab headers axios
        // might attach so verifiers on a shared device don't accidentally
        // get blocked by tab-token enforcement.
        const res = await API.get(`/billing/verify/${encodeURIComponent(token)}`, {
          headers: { Authorization: "", "X-Tab-Token": "", "X-Tab-Token-Skip": "1" },
          transformRequest: [(d, h) => { delete h.Authorization; delete h["X-Tab-Token"]; return d; }],
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "We couldn't verify this link.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <Centered>
        <Loader2 className="w-7 h-7 animate-spin text-purple-600" />
        <p className="text-sm text-slate-500 mt-3">Verifying with GLPFlo…</p>
      </Centered>
    );
  }

  if (error || !data) {
    return (
      <Centered>
        <Card className="border-rose-200 max-w-md w-full" data-testid="benefits-verify-error">
          <CardContent className="p-7 text-center">
            <span className="w-14 h-14 rounded-full bg-rose-100 mx-auto flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7 text-rose-600" />
            </span>
            <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Verification link invalid
            </h1>
            <p className="text-sm text-slate-500 leading-relaxed">{error}</p>
            <p className="text-xs text-slate-400 mt-4">
              Links expire after 90 days. Please ask the patient for a fresh link
              from their GLPFlo dashboard, or contact customer.service@glpflo.com.
            </p>
          </CardContent>
        </Card>
      </Centered>
    );
  }

  const pay = data.payments_last_6_months || {};

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white">
      {/* Brand strip */}
      <header className="bg-white border-b border-slate-200 print:hidden">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-violet-600 flex items-center justify-center">
            <Pill className="w-4 h-4 text-white" />
          </span>
          <span className="font-extrabold text-slate-900 text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP<span className="text-purple-600">Flo</span>
          </span>
          <span className="ml-auto text-xs text-slate-400 uppercase tracking-wider">Benefits verification</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 print:py-4">
        {/* Verified hero */}
        <div className="bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-7 text-center mb-5 print:border-2 print:bg-white" data-testid="benefits-verify-hero">
          <span className="inline-flex w-16 h-16 rounded-full bg-emerald-500 items-center justify-center mb-3 shadow-lg shadow-emerald-200">
            <ShieldCheck className="w-9 h-9 text-white" />
          </span>
          <h1 className="text-3xl font-extrabold text-emerald-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Verified by GLPFlo
          </h1>
          <p className="text-sm text-emerald-700">
            This patient holds an active or recently-active prescription account.
          </p>
          <p className="text-[11px] text-emerald-600/80 mt-3" data-testid="benefits-verify-verified-at">
            Verified {fmtVerifiedAt(data.verified_at)}
          </p>
        </div>

        {/* Patient summary */}
        <Card className="mb-5">
          <CardContent className="p-5 sm:p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Patient summary</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label="Initials" value={data.patient_initials || "—"} testid="bv-initials" />
              <Stat label="Account #" value={data.account_masked || "—"} testid="bv-account" />
              <Stat label="State" value={data.patient_state || "—"} testid="bv-state" />
              <Stat
                label="Active subs"
                value={String(data.active_subscription_count ?? 0)}
                icon={<BadgeCheck className="w-3 h-3" />}
                testid="bv-subs"
              />
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-2 gap-4">
              <Stat
                label="Payments · last 6 mo"
                value={String(pay.count ?? 0)}
                icon={<Calendar className="w-3 h-3" />}
                testid="bv-pay-count"
              />
              <Stat
                label="Total · last 6 mo"
                value={fmtMoney(pay.total_usd ?? 0)}
                emphasis
                testid="bv-pay-total"
              />
            </div>
          </CardContent>
        </Card>

        {/* About GLPFlo */}
        <Card className="mb-5">
          <CardContent className="p-5 sm:p-6">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">About {data.platform?.name || "GLPFlo"}</h2>
            <p className="text-sm text-slate-700 leading-relaxed">{data.platform?.description}</p>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
              {data.platform?.contact_email && (
                <a href={`mailto:${data.platform.contact_email}`} className="inline-flex items-center gap-1.5 hover:text-purple-600">
                  <Mail className="w-3.5 h-3.5" /> {data.platform.contact_email}
                </a>
              )}
              {data.platform?.contact_phone && (
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" /> {data.platform.contact_phone}
                </span>
              )}
              {data.platform?.site && (
                <a href={data.platform.site} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-purple-600">
                  <Globe className="w-3.5 h-3.5" /> {data.platform.site.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Disclosures */}
        {Array.isArray(data.disclosures) && data.disclosures.length > 0 && (
          <Card>
            <CardContent className="p-5 sm:p-6">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Disclosures</h2>
              <ul className="space-y-2">
                {data.disclosures.map((d, i) => (
                  <li key={i} className="text-xs text-slate-600 leading-relaxed flex gap-2">
                    <span className="text-purple-500 mt-1">•</span> <span>{d}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-[10px] text-slate-400 mt-6 print:mt-4">
          This page is signed by GLPFlo · Verifier IP and time are recorded for audit.
        </p>
      </main>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="flex flex-col items-center w-full">{children}</div>
    </div>
  );
}

function Stat({ label, value, icon, emphasis, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
        {icon} {label}
      </p>
      <p className={`mt-1 ${emphasis ? "text-xl font-extrabold text-emerald-600" : "text-base font-bold text-slate-900"}`}>
        {value}
      </p>
    </div>
  );
}
